/**
 * LA CARTE « ANTI-ROBOTS » DE L'ADMINISTRATEUR — AB#884.
 *
 * Les comptes signalés pour avoir ouvert beaucoup de contenus DISTINCTS du
 * manuel en peu de temps (sur 24 h et sur 7 jours), les blocages de lecture en
 * cours — levables d'un geste —, et les compteurs du défi Turnstile.
 * Des identifiants de compte et des nombres : aucun nom, aucune adresse.
 * Lecture : `GET /v1/admin/anti-robots` ; levée :
 * `POST /v1/admin/anti-robots/comptes/:compteId/levee`.
 *
 * La carte charge ses propres données : la page n'a qu'à l'insérer.
 */
import type { EtatAntiRobotsResponse } from '@silex/shared';
import { useCallback, useState } from 'react';

import { api } from '../api/client';
import { messageErreur, useAsyncRechargeable } from '../lib/useAsync';

import { Chargement, ErreurRessource } from './EtatRessource';

const MODES: Readonly<Record<EtatAntiRobotsResponse['reglages']['aspiration'], string>> = {
  non: 'détection éteinte',
  alerte: 'détection en alerte seule',
  blocage: 'détection avec blocage',
};

export function heureLocale(iso: string): string {
  return new Intl.DateTimeFormat('fr-CA', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'America/Montreal',
  }).format(new Date(iso));
}

export function CarteAntiRobotsVue({
  data,
  onLever,
  enCours,
  erreurLevee,
}: {
  readonly data: EtatAntiRobotsResponse;
  readonly onLever: (compteId: string) => void;
  readonly enCours: string | null;
  readonly erreurLevee: string | null;
}) {
  const r = data.reglages;
  return (
    <>
      <p className="suivi-note" data-testid="anti-robots-signales">
        Comptes signalés :{' '}
        <strong style={{ fontSize: 20, color: 'var(--ink)' }}>{data.comptesSignales.jour}</strong>{' '}
        sur 24 h, {data.comptesSignales.semaine} sur 7 jours — {MODES[r.aspiration]}, défi{' '}
        {r.defi === 'oui' && r.defiCleSecretePosee ? 'actif' : 'éteint'}.
      </p>
      {erreurLevee ? (
        <p className="form-error" role="alert">
          {erreurLevee}
        </p>
      ) : null}
      {data.blocagesActifs.length === 0 ? (
        <p className="suivi-note">Aucun blocage de lecture en cours.</p>
      ) : (
        <div className="table-wrap" style={{ marginTop: 10 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Compte</th>
                <th scope="col">Contenus distincts</th>
                <th scope="col">Bloqué jusqu’à</th>
                <th scope="col" aria-label="Action" />
              </tr>
            </thead>
            <tbody>
              {data.blocagesActifs.map((b) => (
                <tr key={b.id}>
                  <td>
                    <code>{b.compteId}</code> ({b.role})
                  </td>
                  <td>
                    {b.distincts} en 1 {b.fenetre} (seuil {b.seuil})
                  </td>
                  <td>{b.bloqueJusqua ? heureLocale(b.bloqueJusqua) : '—'}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn--sm"
                      disabled={enCours === b.compteId}
                      onClick={() => onLever(b.compteId)}
                    >
                      {enCours === b.compteId ? 'Levée…' : 'Lever le blocage'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="suivi-note" style={{ marginTop: 8, fontSize: 12 }}>
        Seuils : alerte à {r.seuils.heure.alerte} contenus distincts par heure ou{' '}
        {r.seuils.jour.alerte} par jour, blocage ({r.dureeBlocageMinutes} min) à{' '}
        {r.seuils.heure.blocage} ou {r.seuils.jour.blocage}. Défi (cette réplique, depuis{' '}
        {heureLocale(data.defi.depuis)}) : {data.defi.verifies} vérifiés, {data.defi.refuses}{' '}
        refusés, {data.defi.replisPermissifs} replis permissifs.
      </p>
    </>
  );
}

export function CarteAntiRobots() {
  const charger = useCallback(() => api.etatAntiRobots(), []);
  const { resource, recharger } = useAsyncRechargeable<EtatAntiRobotsResponse>(charger);
  const [enCours, setEnCours] = useState<string | null>(null);
  const [erreurLevee, setErreurLevee] = useState<string | null>(null);

  const lever = useCallback(
    (compteId: string) => {
      setEnCours(compteId);
      setErreurLevee(null);
      api
        .leverBlocageLecture(compteId)
        .then(() => recharger())
        .catch((e: unknown) => setErreurLevee(`Levée impossible : ${messageErreur(e)}`))
        .finally(() => setEnCours(null));
    },
    [recharger],
  );

  return (
    <section
      className="card"
      aria-label="Anti-robots"
      style={{ margin: 0, gridColumn: '1 / -1' }}
      data-testid="carte-anti-robots"
    >
      <h2 style={{ fontSize: 17, margin: '0 0 6px' }}>Anti-robots — lecture du manuel</h2>
      {resource.status === 'loading' ? <Chargement quoi="les signalements" /> : null}
      {resource.status === 'error' ? <ErreurRessource message={resource.message} /> : null}
      {resource.status === 'ready' ? (
        <CarteAntiRobotsVue
          data={resource.data}
          onLever={lever}
          enCours={enCours}
          erreurLevee={erreurLevee}
        />
      ) : null}
    </section>
  );
}
